export interface FitOptions {
  /** Lower bound for the search (px). Default 8. */
  min?: number;
  /** Upper bound for the search (px). Default 96. */
  max?: number;
  /** Rounding granularity for the result (px). Default 1. */
  step?: number;
}

/**
 * Dimensions the text would take at a given font size — returned by the
 * injected measurement function. `lines` is the number of wrapped lines
 * the text produces at that width.
 */
export interface TextMetrics {
  width: number;
  height: number;
}

export type MeasureFn = (text: string, fontSize: number, maxWidth: number) => TextMetrics;

/**
 * Find the largest font size where `text` still fits into a box of
 * `width × height` pixels, using binary search between [min, max].
 *
 * The measurement is injected so this function is deterministic and
 * testable: in production the board supplies a Konva-Text-based
 * measurer; in tests a synthetic one.
 *
 * A text that doesn't fit even at the minimum size returns `min` (the
 * text will clip — acceptable, we never want it bigger than the sticky
 * can hold). Empty text returns `max`.
 */
export function fitFontSize(
  text: string,
  width: number,
  height: number,
  measure: MeasureFn,
  options: FitOptions = {},
): number {
  const min = options.min ?? 8;
  const max = options.max ?? 96;
  const step = options.step ?? 1;

  if (width <= 0 || height <= 0) return min;
  if (text.trim().length === 0) return max;

  // Binary search on integer-stepped font sizes.
  let lo = min;
  let hi = max;
  let best = min;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / (2 * step)) * step;
    const m = measure(text, mid, width);
    if (m.width <= width && m.height <= height) {
      best = mid;
      lo = mid + step;
    } else {
      hi = mid - step;
    }
  }
  return best;
}
